import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useQueryParams } from 'hooks/useQueryParams';
import { useFreecodeContextRun, useFreecodeContextData } from 'hooks/useContext';
import services from 'api/services';
import { sendAnalytics } from 'functions/sendAnalytics';
const { FreecodeRunService } = services;

export default function useExerciseRunFunction({ setShowNextLevelFlowToRunModal }) {
  const { exerciseId } = useParams();
  const { courseId } = useQueryParams();

  const [isLevelCompleted, setIsLevelCompleted] = useState(false);
  const [isRunCodePressedForCurrentLevel, setIsRunCodePressedForCurrentLevel] = useState(false);

  const { testImpl, setInput, setCourse } = useFreecodeContextData();
  const {
    setRunFunction,
    updateRunResponse,
    setIsRunning,
    isRunningRef,
    codeSolRef,
    languageRef,
    levelToRunRef,
    isCanvasAnimationCompleted,
    setIsCanvasAnimationCompleted,
    levelToRun,
  } = useFreecodeContextRun();

  const runFlowTimeout = useRef(null);
  const interactFlowTimeout = useRef(null);
  const currentLevel = useRef(levelToRun);

  useEffect(() => {
    currentLevel.current = levelToRun;
    setIsRunCodePressedForCurrentLevel(false);
    setIsLevelCompleted(false);
  }, [levelToRun]);

  useEffect(() => {
    if (!isLevelCompleted || !isRunCodePressedForCurrentLevel || levelToRun !== currentLevel.current) {
      clearTimeout(runFlowTimeout.current);
      return clearTimeout(interactFlowTimeout.current);
    }
    let totalLevels = testImpl?.configLevels?.numPublic + testImpl?.configLevels?.numHidden;
    if (levelToRun + 1 >= totalLevels) return;
    if (isCanvasAnimationCompleted) {
      clearTimeout(runFlowTimeout.current);
      interactFlowTimeout.current = setTimeout(() => setShowNextLevelFlowToRunModal(true), 3000);
    } else {
      runFlowTimeout.current = setTimeout(() => setShowNextLevelFlowToRunModal(true), 12000);
    }
    currentLevel.current = levelToRun;
  }, [
    isLevelCompleted,
    isCanvasAnimationCompleted,
    setShowNextLevelFlowToRunModal,
    levelToRun,
    isRunCodePressedForCurrentLevel,
    testImpl?.configLevels,
  ]);

  const runFunction = useCallback(() => {
    if (isRunningRef.current) return;
    setIsRunning(true);
    const doRun = async () => {
      try {
        let runBody = {
          test: exerciseId,
          code: codeSolRef.current,
          language: languageRef.current,
          levelToRun: levelToRunRef.current,
        };
        if (courseId) runBody.course = courseId;

        sendAnalytics('runExercise', exerciseId);
        setIsCanvasAnimationCompleted(false);
        const runResult = await FreecodeRunService.runExercise(runBody);

        setIsRunCodePressedForCurrentLevel(true);
        const decompressed = updateRunResponse(runResult.body);

        if (runResult.input) setInput(runResult.input);

        let totalLevels = testImpl?.configLevels?.numPublic + testImpl?.configLevels?.numHidden;

        if (decompressed.scores[0] === decompressed.maxScore && totalLevels !== levelToRunRef.current + 1)
          setIsLevelCompleted(true);

        if (decompressed.scores[0] === decompressed.maxScore) {
          // Update course progress if applicable
          setCourse((oldCourse) => {
            try {
              if (!oldCourse) return;
              let course = { ...oldCourse };
              for (const index in course.syllabus) {
                if (!course.syllabus[index]?.modules?.length) return;
                for (const moduleIndex in course.syllabus[index].modules) {
                  let exercise = course.syllabus[index].modules[moduleIndex]?.exercise;
                  if (exercise?.id === exerciseId) {
                    if (exercise?.languagesCompleted?.[languageRef.current]?.[decompressed.index]) {
                      exercise.languagesCompleted[languageRef.current][decompressed.index].isCompleted = true;
                    }
                    if (decompressed.index + 1 >= totalLevels) {
                      exercise.isCompletedInAnyLanguage = true;
                    }
                    course.syllabus[index].modules[moduleIndex].exercise = exercise;
                    return course;
                  }
                }
              }
              return course;
            } catch (error) {
              return oldCourse;
            }
          });
        }

        if (runResult.toastEvent) {
          if (runResult.toastEvent.exerciseCompletion) {
            sendAnalytics('exerciseCompletion', exerciseId);
          }
          if (runResult.toastEvent.amount)
            toast.info(`You've gained ${runResult.toastEvent.amount} XP for Exercise's Level Completion!`);
          if (runResult.toastEvent.exerciseCompletion?.amount)
            toast.info(`You've gained ${runResult.toastEvent.exerciseCompletion.amount} XP for Exercise Completion!`);
        }
        if (runResult.achievementToast?.length) {
          for (const aToast of runResult.achievementToast) {
            if (aToast.toastMessage) toast.info(aToast.toastMessage);
          }
        }
      } catch (error) {
        console.error('Run error:', error);
        toast.error('Run error ' + (error?.message || 'internal Error'));
        updateRunResponse(undefined);
      } finally {
        setIsRunning(false);
      }
    };
    doRun();
  }, [
    isRunningRef,
    setIsRunning,
    exerciseId,
    codeSolRef,
    languageRef,
    levelToRunRef,
    courseId,
    updateRunResponse,
    setInput,
    setCourse,
    testImpl?.configLevels,
    setIsCanvasAnimationCompleted,
  ]);

  useEffect(() => setRunFunction(() => runFunction), [runFunction, setRunFunction]);
}
